import { DecimalPipe } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { ClearBonusComponent } from 'src/app/pages/user-asset/clear-bonus/clear-bonus.component';
import { UserAssetsService } from 'src/app/pages/user-asset/user-assets.service';
import { WithdrawService } from 'src/app/pages/withdraw/withdraw.service';
import { WalletApi } from 'src/app/shared/apis/wallet.api';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { CSInputComponent } from 'src/app/shared/components/cs-input/cs-input.component';
import { CSSelectComponent } from 'src/app/shared/components/cs-select/cs-select.component';
import { InputFloatDirective } from 'src/app/shared/directive/input.directive';
import { ForbidPaymentCategory, ForbidWithdrawType } from 'src/app/shared/interfaces/wallet.interface';
import { PipesModule } from 'src/app/shared/pipes/pipes.module';
import { LocaleService } from 'src/app/shared/service/locale.service';
import { PopupService } from 'src/app/shared/service/popup.service';
import { TokenNetworksValidationService } from 'src/app/shared/service/token-networks-validation';
import { DepositWithdrawForbidComponent } from '../deposit-withdraw-forbid/deposit-withdraw-forbid.component';
import { WalletDialogType, WalletService } from './../../wallet.service';

@UntilDestroy()
@Component({
  selector: 'app-withdraw-transfer',
  standalone: true,
  imports: [
    DecimalPipe,
    CSButtonComponent,
    CSSelectComponent,
    CSInputComponent,
    PipesModule,
    InputFloatDirective,
    DepositWithdrawForbidComponent,
  ],
  templateUrl: './withdraw-transfer.component.html',
  styleUrl: './withdraw-transfer.component.scss',
})
export class WithdrawTransferComponent implements OnInit, OnDestroy {
  constructor(
    public appService: AppService,
    public walletService: WalletService,
    private userAssetsService: UserAssetsService,
    private localeService: LocaleService,
    private tokenNetworksValidationService: TokenNetworksValidationService,
    private withdrawService: WithdrawService,
    private popup: PopupService,
    private walletApi: WalletApi,
  ) {}

  //network variable
  /**當前選擇的網路*/
  selectedNetwork = '';
  /**當前選擇的網路對應的資料*/
  selectedNetworkInfo: any;
  /**當前選擇的地址簿地址對應的資料*/
  selectedAddressInfo: any;
  /**網路列表*/
  networkList: any[] = [];
  /**網路加載狀態*/
  networkLoading = false;
  /** 獲取所有網路，用於地址驗證 */
  allNetworkList: any = [];

  //currency variable
  /**當前選擇的幣種*/
  currentCurrency: string = 'USDT';
  /**虛擬貨幣幣種列表*/
  cryptoList: any[] = [];
  /**貨幣加載狀態*/
  currencyLoading = false;

  /** 用戶餘額 */
  userBalance: any[] = [];
  /** 幣種提款限制 */
  coinLimitData: any;
  /** 幣種提款限制loading */
  coinLimitLoading: boolean = false;
  /** 提款金額輸入 */
  amount: number | string = '';
  /** 提款金額輸入驗證 */
  amountValid: boolean = true;
  /** 提款金額錯誤提示文字 */
  errText: string = '';

  /** 手續費類別 */
  wdFeeType: 'A' | 'C' = 'C';
  /** 手續費C的上限 */
  typeCFeeMax: number = 0;
  /** 手續費C的值*/
  fee: number = 0;

  /** 自行輸入的地址 */
  // manualAddress: string = '0x369c940fF9Ff171d27Dc84A31658daEA7F9C8e85';
  manualAddress: string = '';
  /** 地址簿選的地址 */
  selectedAddress: string = '';
  /** 地址簿地址列表 */
  addressList: any = [];
  /** 地址簿loading */
  addressListLoading: boolean = false;

  /** 使用手動輸入地址 */
  useManualEnter: boolean = true;
  /** 地址驗證 */
  addressValid: {
    isValid: boolean;
    text: string;
    currencyValid: boolean;
    networkValid: boolean;
  } = {
    isValid: true,
    text: '',
    currencyValid: false,
    networkValid: false,
  };
  /** 用地址簿地址提款不需要2fa，在當前頁面就要loading */
  submitLoading: boolean = false;

  // 提款禁止加載狀態
  isForbidLoading: boolean = false;
  isWithdrawForbid: boolean = false;

  async ngOnInit() {
    // 提款前先判断是否禁止提款
    await this.checkWithdrawForbid();

    // this.checkWithdrawValid();
    await this.getCurrencyInfo();
    // 全部網路只要獲取一次
    await this.getAllNetworks();
    // 用戶地址只要獲取一次
    await this.getAddressList();
    this.checkNetworkBySelectedAddress();
    this.appService.sysConfig$.pipe(untilDestroyed(this)).subscribe(x => {
      this.wdFeeType = x?.handlingFeeType ?? 'C';
      this.updateTypeCFeeMax();
    });
  }
  /**
   * 獲取所有幣種與當前選擇幣種
   */
  async getCurrencyInfo() {
    this.currencyLoading = true;
    const result = await firstValueFrom(this.walletService.getCryptoList());
    this.currencyLoading = false;
    this.cryptoList = result?.cryptoList || [];
    this.currentCurrency = result?.currentCurrency || 'USDT';
    await Promise.all([this.getNetworkList(), this.getCoinLimit()]);
  }

  /**
   * 獲取用戶餘額
   */
  async getUserBalance() {
    this.userBalance = (await this.userAssetsService.getUserbalance()).filter((item: any) => item.isDigital);
  }

  /**
   * 獲取網路列表
   * withdraw
   */
  async getNetworkList() {
    this.networkLoading = true;
    this.networkList = await this.walletService.getNetwork('Withdraw', this.currentCurrency);
    this.networkLoading = false;
    this.selectedNetwork = this.networkList[0]?.value || '';
    this.selectedNetworkInfo = this.networkList.find(x => x.value == this.selectedNetwork).networkInfo;
  }

  /**
   * 獲取幣種提款限制
   */
  async getCoinLimit() {
    this.coinLimitLoading = true;
    this.coinLimitData = await this.walletService.getCoinLimit(this.currentCurrency);
    this.coinLimitLoading = false;
  }

  @HostListener('document:keydown', ['$event'])
  public onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && this.isAllLoaded() && !this.isWithdrawDisabled()) {
      this.submit();
    }
  }

  /**
   * 處理幣種變更邏輯
   * cs-select的change currency事件
   */
  async currencyChange() {
    await this.getNetworkList();
    await this.getCoinLimit();
    this.updateTypeCFeeMax();
    this.checkNetworkBySelectedAddress();
    this.reset();
  }

  /**
   * cs-select的change network事件
   */
  networkChange() {
    this.selectedNetworkInfo = this.networkList.find(x => x.value == this.selectedNetwork).networkInfo;
    this.onAddressInput(this.manualAddress);
  }

  addressChange() {
    this.selectedAddressInfo = this.addressList.find((item: any) => item.value == this.selectedAddress);
    this.updateSelectedNetworkInfo();
  }

  /**
   * 是否所有資料都已經加載完成
   *
   * @returns
   */
  isAllLoaded(): boolean {
    return !this.networkLoading && !this.coinLimitLoading && !this.addressListLoading && !this.submitLoading;
  }
  isWithdrawDisabled(): boolean {
    const amountInvalid = !this.amountValid || this.amount === '';
    if (this.walletService.useManualEnter) {
      return amountInvalid || !this.addressValid.isValid || this.manualAddress === '';
    }
    return amountInvalid || this.selectedAddress === '';
  }
  goSavedAddress() {
    this.walletService.setWalletDialogType(WalletDialogType.SAVED_ADDRESS);
  }
  /**
   * 选择提款全部金额
   */
  max() {
    this.amount = Number(this.coinLimitData?.availQuota || 0);
    this.onAmountInput();
  }
  /**
   * 設置實際當賬金額
   * 包含扣除網路手續費與平台手續費
   */
  setTypeCFee() {
    if (isNaN(Number(this.amount))) {
      return;
    }
    // 有免費提款次數
    if (this.coinLimitData.withdrawCount > 0) {
      this.fee = 0;
    } else {
      // 没有免费提款次数，要用百分比去计算手续费
      this.fee = Number(this.amount).subtract(this.coinLimitData.withdrawalFeeProportion).divide(100);
      // 如果百分比手续费大于最大手续费，则使用最大手续费
      if (this.fee > this.typeCFeeMax) {
        this.fee = this.typeCFeeMax;
      }
    }
  }

  /**
   * 地址输入
   *
   * @param element
   * @param event
   */
  onAddressInput(element: any) {
    // 如果沒有輸入就清空所有狀態
    if (this.manualAddress === '') {
      this.addressValid.isValid = true;
      this.addressValid.text = '';
      return;
    }
    //根據幣種檢查地址
    if (this.currentCurrency) {
      this.addressValid.currencyValid = Boolean(
        this.tokenNetworksValidationService.checkAdressVailedByCurrency(this.currentCurrency, this.manualAddress),
      );
    } else {
      this.addressValid.currencyValid = false;
    }

    // 根據網路查地址
    if (this.selectedNetwork) {
      this.addressValid.networkValid = Boolean(
        this.tokenNetworksValidationService.checkNetWorkValied(
          this.selectedNetworkInfo.name,
          this.selectedNetworkInfo.network,
          this.manualAddress,
        ),
      );
    } else {
      this.addressValid.networkValid = false;
    }
    //判断显示文字
    if (this.manualAddress.length === 0 && (element.foc || element === '')) {
      this.addressValid.isValid = false;
      this.addressValid.text = this.localeService.getValue('enter_wd_ad');
    } else if (!this.addressValid.currencyValid) {
      this.addressValid.isValid = false;
      this.addressValid.text = 'Address format incorrect. Please verify its length and format before trying again.';
    } else if (this.selectedNetwork && !this.addressValid.networkValid) {
      this.addressValid.isValid = false;
      this.addressValid.text = 'Please ensure that the wallet address matches the selected network.';
    } else {
      this.addressValid.isValid = true;
    }
  }

  /**
   * 金額輸入的綁定
   *
   * @returns
   */
  onAmountInput() {
    // 完全不輸入金額時也不要顯示錯誤訊息
    if (this.amount === '') {
      this.amountValid = true;
      this.errText = '';
      this.fee = 0;
      return;
    }

    if (this.coinLimitData?.availQuota > 0) {
      this.setTypeCFee();
    }

    // 檢查輸入金額是否小於單筆最小提款金額
    if (Number(this.amount) < this.selectedNetworkInfo?.minAmount) {
      this.errText = 'The withdrawal amount is below the minimum limit';
      this.amountValid = false;
      // 檢查輸入金額是否大於可用提款額度
    } else if (Number(this.amount) > this.coinLimitData?.availQuota) {
      this.errText = 'The withdrawal amount exceeds your available balance';
      this.amountValid = false;
      // 檢查輸入金額是否大於單筆最大提款金額
    } else if (Number(this.amount) > this.selectedNetworkInfo?.maxAmount) {
      this.errText = 'The withdrawal amount exceeds the maximum limit';
      this.amountValid = false;
      // 金額輸入正確，清除錯誤訊息
    } else {
      this.amountValid = true;
      this.errText = '';
    }
  }

  /**
   * 重置
   * 提款金額 提款認證 提款錯誤訊息
   */
  reset() {
    this.amount = '';
    this.amountValid = true;
    this.errText = '';

    // this.manualAddress = '';
    this.onAddressInput(this.manualAddress);
  }

  /**
   * 轉換手續費C的上限為當前幣種
   */
  updateTypeCFeeMax() {
    if (this.wdFeeType !== 'C') return;
    const allRate = this.userAssetsService.allRate.value;
    if (allRate && this.currentCurrency) {
      const currFiatRate = allRate.rates.find((rate: any) => rate.currency === this.currentCurrency);
      this.typeCFeeMax = (this.coinLimitData?.perFeeLimit || 0).divide(currFiatRate?.rate || 1);
    }
  }

  /**
   * 自行輸入地址：需要2fa
   * 從地址簿選擇：不用2fa
   * 添加地址：需要2fa
   */
  submit() {
    if (this.walletService.useManualEnter) {
      this.walletService.otpType = 'Withdraw';
      this.walletService.email = this.appService.userInfo$.getValue()?.email || '';
      this.walletService.preservedWithdrawState = {
        currentCurrency: this.currentCurrency,
        selectedNetwork: this.selectedNetwork,
        address: this.manualAddress,
        amount: Number(this.amount),
      };
      this.walletService.setWalletDialogType(WalletDialogType.VERIFY_2FA);
    } else {
      this.walletService.preservedWithdrawState = {
        currentCurrency: this.currentCurrency,
        selectedNetwork: this.selectedAddressInfo?.network || '',
        address: this.selectedAddressInfo?.value || '',
        addressId: this.selectedAddressInfo?.addressId || '',
        amount: Number(this.amount),
      };
      this.submitLoading = true;
      this.walletService.cryptoWithdraw('');
    }
  }

  /**
   * 检查是否可以提款
   * 舊流程UI還要改
   */
  checkWithdrawValid() {
    this.withdrawService.allowWithdrawal().subscribe(data => {
      if (!data) {
        // 取消，=> 跳转钱包总览
        // 清零，=> 直接弹清零弹窗 => 成功后回调再次检查执行 checkWithdrawValid
        this.popup
          .open(ClearBonusComponent, { speed: 'faster' })
          .beforeClosed()
          .subscribe(e => {
            if (e) {
              this.checkWithdrawValid();
            } else {
              // this.router.navigateByUrl(`/${this.appService.languageCode}/wallet/overview`);
            }
          });
      }
    });
  }

  /**
   * 切換地址輸入模式
   */
  async switchAddressMode() {
    this.walletService.useManualEnter = !this.walletService.useManualEnter;
    this.updateSelectedNetworkInfo();
    this.reset();
  }

  /**
   * 獲取地址列表
   */
  async getAddressList() {
    const params = {
      currency: '',
      isWhiteList: null,
      isUniversalAddress: null,
      address: '',
      isWithdraw: true,
      walletAddressType: '',
    };
    this.addressListLoading = true;
    const data = await this.walletService.loadAddressList(params);

    this.addressList = data.map((item: any) => {
      const matchedData = this.networkList.find((network: any) => network.value === item.network);
      console.log('matched', matchedData);
      return {
        data: {
          labelA: item.remark,
          labelB: this.walletService.shortenAddress(item.address),
        },
        value: item.address,
        network: item.network,
        addressId: item.id,
      };
    });

    this.addressListLoading = false;
  }

  /**
   * 獲取全部網路
   */
  async getAllNetworks() {
    this.networkLoading = true;
    if (this.walletService.allNetworkList.length == 0) {
      await this.walletService.getAllNetworks();
    }
    const tmp = this.walletService.allNetworkList;
    this.allNetworkList = tmp;
    this.networkLoading = false;
  }

  /**
   * 驗證選擇的地址跟當前幣種支持的網路有沒有匹配
   */
  checkNetworkBySelectedAddress() {
    // 根據 networkList 的 value 給 addressList 設置 disabled 屬性
    this.addressList = this.addressList.map((address: any) => {
      const matched = this.networkList.some((network: any) => network.value === address.network);
      return {
        ...address,
        disabled: !matched,
      };
    });
    // 過濾掉 disabled 的地址
    const onlyAvailableAddress = this.addressList.filter((item: any) => !item.disabled) || [];
    // 預設第一個非disabled的地址
    this.selectedAddressInfo = onlyAvailableAddress[0];
    this.selectedAddress = this.selectedAddressInfo?.value || '';
    this.updateSelectedNetworkInfo();
  }

  /**
   * 更新網路手續費與最低存款金額
   */
  updateSelectedNetworkInfo() {
    if (!this.walletService.useManualEnter) {
      this.selectedNetworkInfo =
        this.networkList.find(x => x.value === this.selectedAddressInfo?.network)?.networkInfo || undefined;
    } else {
      this.selectedNetworkInfo = this.networkList[0]?.networkInfo || undefined;
    }
  }
  ngOnDestroy(): void {
    // this.walletService.useManualEnter = true; // 重置手動輸入狀態
  }

  /**
   * 提款前先判断是否禁止提款
   */
  async checkWithdrawForbid() {
    this.isForbidLoading = true;
    this.isWithdrawForbid = await await firstValueFrom(
      this.walletApi.forbidPaymentInfo({
        category: ForbidPaymentCategory.Withdraw,
        withdrawType: ForbidWithdrawType.Encryption,
      }),
    );
    this.isForbidLoading = false;
  }
}
